import { OpenInFull } from "@mui/icons-material";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { WidgetContainer, type Widget as WidgetType } from "@/lib/api";
import { darkGrey } from "@/theme/colors";
import { Widget, WidgetTabs } from "@/features/dashboard/components";
import useStateFromContext from "@/features/shared/hooks/use-state-from-context";
import useLayoutStore from "@/store/useLayoutStore";

import {
  WidgetContainerContext,
  WidgetContainerContextProps,
  WidgetContainerHeaderActionsProps,
} from "./context";
import * as Styled from "./styles";
import WidgetMeta from "./widget-meta";

type Props = {
  container: WidgetContainer;
  style?: React.CSSProperties;
};

const HeaderActions = ({
  isFullscreen,
  onFullScreenClick,
  widget,
}: {
  isFullscreen: boolean;
  onFullScreenClick: () => void;
  widget: WidgetType;
}) => {
  const widgetContainerContext = useContext(WidgetContainerContext);
  const [headerActions] = useStateFromContext<
    WidgetContainerHeaderActionsProps,
    WidgetContainerContextProps
  >("headerActions", widgetContainerContext);

  return (
    <div>
      {headerActions &&
        Object.entries(headerActions).map(([key, element]) => (
          <React.Fragment key={key}>{element}</React.Fragment>
        ))}
      <WidgetMeta widget={widget} />
      <Styled.FullScreenButton onClick={onFullScreenClick}>
        {isFullscreen ? (
          <CloseFullscreenIcon fontSize="small" htmlColor={darkGrey} />
        ) : (
          <OpenInFull fontSize="small" htmlColor={darkGrey} />
        )}
      </Styled.FullScreenButton>
    </div>
  );
};

const WidgetContainerCard: React.ForwardRefRenderFunction<HTMLDivElement, Props> = (
  { container, ...props },
  ref,
) => {
  const [isFullscreen, layoutStore] = useLayoutStore((s) => s.context.isFullscreen);

  const [currentWidgetFullscreen, setCurrentWidgetFullscreen] = useState(false);
  const [headerActions, setHeaderActions] = useState<WidgetContainerHeaderActionsProps>({});
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const hasTabs = container.widgets.length > 1;
  const activeWidget = container.widgets.at(activeTabIndex) ?? container.widgets.at(0);

  const handleTabChange = useCallback(
    (index: number) => {
      setHeaderActions({});
      setActiveTabIndex(index);
    },
    [setHeaderActions],
  );

  useEffect(() => {
    if (!isFullscreen) {
      setCurrentWidgetFullscreen(false);
    }
  }, [isFullscreen]);

  const handleFullscreen = useCallback(() => {
    if (!layoutStore) return;

    setCurrentWidgetFullscreen((isOpen) => !isOpen);
    layoutStore.trigger.toggleFullscreen();
  }, [layoutStore, setCurrentWidgetFullscreen]);

  if (isFullscreen && !currentWidgetFullscreen) return null;
  if (!activeWidget) return null;

  return (
    <WidgetContainerContext.Provider
      value={{
        headerActions: [headerActions, setHeaderActions],
        currentWidgetFullscreen,
      }}
    >
      <Styled.Card
        isFullscreen={isFullscreen}
        {...props}
        style={
          currentWidgetFullscreen
            ? {
                ...props.style,
                top: 80,
                left: 64,
                width: "calc(100vw - 72px)",
                height: "calc(100vh - 88px)",
                transform: "none",
                zIndex: 0,
              }
            : props.style
        }
        ref={ref}
      >
        <Styled.CardHeader
          title={activeWidget.title}
          action={
            <HeaderActions
              isFullscreen={isFullscreen}
              onFullScreenClick={handleFullscreen}
              widget={activeWidget}
            />
          }
          titleTypographyProps={{
            variant: "h6",
            fontWeight: 700,
          }}
        />
        <Styled.CardContent>
          {hasTabs ? (
            <WidgetTabs
              tabs={container.widgets}
              activeTab={activeTabIndex}
              onTabChange={handleTabChange}
              showTabs={!currentWidgetFullscreen}
            />
          ) : (
            <WidgetContent widget={activeWidget} />
          )}
        </Styled.CardContent>
      </Styled.Card>
    </WidgetContainerContext.Provider>
  );
};

export const WidgetContentContext = createContext<{
  ref: React.RefObject<HTMLDivElement> | null;
}>({
  ref: null,
});

const WidgetContent = ({ widget }: { widget: WidgetType }) => {
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <WidgetContentContext.Provider
      value={{
        ref: contentRef,
      }}
    >
      <div style={{ flexGrow: 1, height: "100%" }} ref={contentRef}>
        <Widget widget={widget} />
      </div>
    </WidgetContentContext.Provider>
  );
};

export default React.forwardRef(WidgetContainerCard);
